import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';

import { MAPBOX_FORWARD_GEOCODING_URL, formatMapboxFeatures } from 'utils/mapbox';
import styles from './styles.module.scss';

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAP_API_TOKEN;
const MAPBOX_DEBOUNCE_DELAY = 400;
const MIN_SYMBOL = 2;

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M10.2462 9.00407L7.35193 6.11131L10.2462 3.21856C10.4108 3.05405 10.5032 2.83094 10.5032 2.59829C10.5032 2.36565 10.4108 2.14253 10.2462 1.97803C10.0816 1.81352 9.85835 1.72111 9.62559 1.72111C9.39283 1.72111 9.1696 1.81352 9.00501 1.97803L6.11077 4.87079L3.21687 1.97837C3.1382 1.89205 3.04237 1.82309 2.93551 1.7759C2.82864 1.72872 2.71311 1.70435 2.59628 1.70435C2.47946 1.70435 2.36392 1.72872 2.25706 1.7759C2.1502 1.82309 2.05437 1.89205 1.9757 1.97837C1.89061 2.05776 1.82277 2.15379 1.77638 2.26049C1.72999 2.36719 1.70605 2.48229 1.70605 2.59863C1.70605 2.71497 1.72999 2.83007 1.77638 2.93678C1.82277 3.04348 1.89061 3.13951 1.9757 3.2189L4.86994 6.11165L1.9757 9.00441C1.89061 9.0838 1.82277 9.17983 1.77638 9.28653C1.72999 9.39323 1.70605 9.50833 1.70605 9.62467C1.70605 9.74101 1.72999 9.85611 1.77638 9.96282C1.82277 10.0695 1.89061 10.1656 1.9757 10.2449C2.05679 10.3271 2.15341 10.3923 2.25995 10.4369C2.36648 10.4814 2.48081 10.5043 2.59628 10.5043C2.71176 10.5043 2.82608 10.4814 2.93262 10.4369C3.03915 10.3923 3.13577 10.3271 3.21687 10.2449L6.11111 7.35218L9.00535 10.2449C9.17061 10.4082 9.39359 10.4997 9.62593 10.4997C9.85827 10.4997 10.0812 10.4082 10.2465 10.2449C10.3288 10.1639 10.394 10.0672 10.4386 9.96073C10.4832 9.8542 10.5061 9.73989 10.5061 9.62443C10.506 9.50898 10.483 9.39468 10.4384 9.28818C10.3938 9.18168 10.3285 9.08511 10.2462 9.00407Z" fill="white"/>
  </svg>
);

function AdditionalAddressRow({ id, address, onAddressChange, onRemove }) {
  const { t } = useTranslation();
  const lang = useSelector((state) => state.settings.lang);

  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const searchTimeoutRef = useRef(null);
  const mapboxAbortRef = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (mapboxAbortRef.current) {
        mapboxAbortRef.current.abort();
      }
    };
  }, []);

  const loadOptions = (searchValue, callback) => {
    setInputValue(searchValue);
    if (searchValue.length <= MIN_SYMBOL) {
      callback(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    if (!MAPBOX_ACCESS_TOKEN) {
      console.warn('Missing Mapbox access token');
      callback([]);
      setIsLoading(false);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (mapboxAbortRef.current) {
      mapboxAbortRef.current.abort();
    }

    searchTimeoutRef.current = setTimeout(() => {
      const params = new URLSearchParams({
        q: searchValue,
        access_token: MAPBOX_ACCESS_TOKEN,
        language: lang,
        limit: '5',
        types: 'country,postcode,place,locality,neighborhood,address',
      });

      mapboxAbortRef.current = new AbortController();

      fetch(`${MAPBOX_FORWARD_GEOCODING_URL}?${params.toString()}`, {
        signal: mapboxAbortRef.current.signal,
      })
        .then((response) => response.json())
        .then((result) => {
          if (result?.features?.length) {
            callback(formatMapboxFeatures(result.features));
            return;
          }
          callback([]);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            callback([]);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, MAPBOX_DEBOUNCE_DELAY);
  };

  const handleAddressChange = useCallback(
    (selectedOption) => {
      onAddressChange(id, selectedOption);
    },
    [id, onAddressChange]
  );

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    control: (provided) => ({
      ...provided,
      borderRadius: '10px',
      border: '1px solid #959baa',
      minHeight: '42px',
    }),
  };

  return (
    <div className={styles.additionalAddressRow}>
      <AsyncSelect
        cacheOptions
        isLoading={isLoading}
        value={address}
        getOptionLabel={(option) => option?.value?.full_address || option?.label || ''}
        loadingMessage={() => t('loading')}
        noOptionsMessage={() => (inputValue.length > MIN_SYMBOL ? t('cargoFields.itinerary_section.no_option') : null)}
        placeholder={t('forms.startEnter', 'Начните вводить')}
        classNamePrefix="select"
        loadOptions={loadOptions}
        onChange={handleAddressChange}
        isClearable
        components={{
          DropdownIndicator: null,
        }}
        styles={customStyles}
        className={styles.additionalCity}
      />
      <button
        type="button"
        className={styles.removeBtn}
        onClick={() => onRemove(id)}
        aria-label={t('common.remove', 'Удалить')}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

AdditionalAddressRow.propTypes = {
  id: PropTypes.number.isRequired,
  address: PropTypes.object,
  onAddressChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

AdditionalAddressRow.defaultProps = {
  address: null,
};

export default AdditionalAddressRow;
